import { Box, Button, IconButton } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import SendIcon from "@mui/icons-material/Send";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FilterSearch from "./components/chat/FilterSearch";
import ContentEditable from "react-contenteditable";
import { XMarkIcon } from "@heroicons/react/24/solid";
import StopCircleIcon from "@mui/icons-material/StopCircle";

import {
  BotMessage,
  BotThinking,
  HumanMessage,
} from "./components/chat/message";
import MagicMycQResponse from "./components/faqs/MagicMycQResponse";
import { Dialog, DialogBody } from "@material-tailwind/react";
import { deleteChatHistory } from "./store/messages/actions";
import { resetChat, saveMsgRefs } from "./store/dataFlow/actions";
import { uniqueId } from "lodash";
import ChatOverlays from "./components/chat/OverLays/ChatOverlays";
import { IoIosArrowDown } from "react-icons/io";

const ChatUI = () => {
  const dispatch = useDispatch();
  const { isResetChat, sidebarWeb } = useSelector((state) => state.dataFlow);
  const [isMaximizedChat, setIsMaximizedChat] = useState(false);
  const [streamingMsg, setStreamingMsg] = useState("");
  const { user } = useSelector((state) => state.auth);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [botMsg, setBotMsg] = useState("");
  const [tagLoader, setTagLoader] = useState(null);
  const [trigger, setTrigger] = useState(null);
  const chatMessagesRef = useRef(null);
  const socketRef = useRef(null);
  const inputBoxRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true); // Add this state

  const connectSocket = () => {
    if (socketRef.current) {
      socketRef.current.close();
    }

    socketRef.current = new WebSocket("wss://api.entheogpt.com/ws");

    socketRef.current.onopen = () => {
      // Request chat history on connection
      socketRef.current.send(
        JSON.stringify({
          type: "get_chat_history",
          kcId: user.id,
          finalId: "last",
          fName: user.firstName,
          // intent: docType,
        })
      );
    };

    socketRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === "chat_history") {
        setMessages(
          message.data.map((msg) => ({
            content: msg.content,
            role: msg.role,
            feedback: msg.feedback,
            id: msg.id,
            isApproved: msg.isApproved,
            kcId: msg.kcId,
            refer: msg.refer ? JSON.parse(msg.refer) : null,
          }))
        );
      } else if (message.type === "bot_message") {
        setLoading(false);
        setIsStreaming(true);
        appendStreamMessage(message.content);
      } else if (message.type === "statusMessage") {
        setStreamingMsg(message.message);
      } else if (message.type === "intent") {
        if (
          [
            "start_psiloscreen",
            "start_entheodna",
            "handle_billing",
            "contact_me",
            "trigger_emergency_notice",
            "submit_feedback",
          ].includes(message.intent)
        ) {
          setTrigger(message.intent);
        }
        if (["rag_science", "rag_law", "rag_wisdom"].includes(message.intent)) {
          setTagLoader(message.intent);
        }
      } else if (message.type === "saved-messages") {
        setStreamingMsg(null);
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          if (updatedMessages.length > 0) {
            updatedMessages[updatedMessages.length - 1] = {
              content: message.messages[1].content,
              feedback: message.messages[1].feedback,
              intent: trigger,
              id: message.messages[1].id,
              tagLoader: tagLoader,
              isApproved: message.messages[1].isApproved,
              kcId: message.messages[1].kcId,
              refer: JSON.parse(message.messages[1].refer),
              role: "assistant",
            };
          }
          return updatedMessages;
        });
        const referData = JSON.parse(message.messages[1].refer);
        if (
          referData &&
          Object.values(referData).some((value) => value.length > 0)
        ) {
          const isRefShow = localStorage.getItem("isRefShow");
          if (isRefShow === "true") {
            dispatch(saveMsgRefs(referData));
          }
        } else {
          dispatch(saveMsgRefs(null));
        }
        // dispatch(saveMsgRefs(JSON.parse(message.messages[1].refer)));
        setBotMsg("");
        setIsStreaming(false);
      }
    };
  };

  useEffect(() => {
    connectSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);

  const sendMessage = (value) => {
    const message = value?.trim();
    setLoading(true);
    if (message) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { content: message, role: "user" },
      ]);

      if (socketRef.current) {
        if (socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.send(
            JSON.stringify({
              kcId: user.id,
              prompt: message,
              model: "dolphin-llama3",
              fName: user.firstName,
            })
          );
        } else {
          console.error(
            "WebSocket is not open. Ready state: ",
            socketRef.current.readyState
          );
          connectSocket();
          socketRef.current.onopen = () => {
            socketRef.current.send(
              JSON.stringify({
                kcId: user.id,
                prompt: message,
                model: "dolphin-llama3",
                fName: user.firstName,
              })
            );
          };
        }
      } else {
        console.error("WebSocket is not initialized.");
        connectSocket();
        socketRef.current.onopen = () => {
          socketRef.current.send(
            JSON.stringify({
              kcId: user.id,
              prompt: message,
              model: "dolphin-llama3",
              fName: user.firstName,
            })
          );
        };
      }

      setInputValue("");
      startBotResponse();
    }
  };

  const disconnectSocket = () => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
      setIsStreaming(false);
      setLoading(false);
      setTagLoader(null);
      botMsg !== "" &&
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          if (updatedMessages.length > 0) {
            updatedMessages[updatedMessages.length - 1] = {
              content: botMsg,
              feedback: "",
              id: uniqueId(),
              tagLoader: "",
              refer: null,
              role: "assistant",
            };
          }
          return updatedMessages;
        });
      setStreamingMsg("");
    }
  };
  const startBotResponse = () => {
    setMessages((prevMessages) => [
      ...prevMessages,
      { content: "", role: "bot" },
    ]);
  };

  const appendStreamMessage = (content) => {
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage.role === "bot") {
        const updatedMessages = [...prevMessages];
        updatedMessages[updatedMessages.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + content,
        };
        setBotMsg(updatedMessages[updatedMessages.length - 1].content);
        return updatedMessages;
      }
      return prevMessages;
    });

    // setBotMsg((prevBotMsg) => prevBotMsg + content);
  };

  const handleChange = (evt) => {
    const value = evt.target.value.replace(/<\/?[^>]+(>|$)/g, "");
    setInputValue(value);
  };

  const deleteAllMessages = () => {
    setMessages([]);
    dispatch(deleteChatHistory());
    dispatch(resetChat(false));

    if (socketRef.current) {
      socketRef.current.close();
    }

    connectSocket();
  };
  useEffect(() => {
    const handleScroll = () => {
      const chatDiv = chatMessagesRef.current;
      if (chatDiv) {
        const isAtBottom =
          chatDiv.scrollHeight - chatDiv.scrollTop === chatDiv.clientHeight;
        if (!isAtBottom) {
          chatDiv.dataset.userScrolling = "true";
        } else {
          chatDiv.dataset.userScrolling = "false";
        }
      }
    };

    const chatDiv = chatMessagesRef.current;
    if (chatDiv) {
      chatDiv.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatDiv) {
        chatDiv.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const chatDiv = chatMessagesRef.current;
    if (chatDiv && chatDiv.dataset.userScrolling !== "true") {
      chatDiv.scrollTop = chatDiv.scrollHeight;
    }
  }, [messages]);
  useEffect(() => {
    if (isResetChat) {
      deleteAllMessages();
    }
  }, [isResetChat]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (e.ctrlKey || e.shiftKey) {
          document.execCommand("insertLineBreak");
        } else {
          sendMessage(inputValue);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [sendMessage]);

  useEffect(() => {
    if (!messagesEndRef.current || !chatMessagesRef.current) return;

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setIsAtBottom(entry.isIntersecting);
      },
      {
        root: chatMessagesRef.current,
        threshold: 1.0,
      }
    );

    observer.observe(messagesEndRef.current);

    return () => {
      if (messagesEndRef.current) observer.unobserve(messagesEndRef.current);
    };
  }, [messages]);
  return (
    <>
      <Box
        id="scrollableDiv"
        className="max-h-[calc(100vh-185px)] relative  md:max-h-[calc(100vh-175px)] lg:max-h-[calc(100vh-150px)] xl:max-h-[calc(100vh-150px)] "
        sx={{
          overflowY: "scroll",
          display: "flex",
          flexDirection: "column-reverse",
          "::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {!isAtBottom && (
          <div
            className="absolute  bottom-0  mb-1 z-10 bg-white rounded-full shadow p-1 cursor-pointer transition bg-clip-padding border text-token-text-secondary border-token-border-default end-1/2 translate-x-1/2 bg-token-main-surface-primary w-12 h-12 sm:w-8 sm:h-8 flex items-center justify-center "
            onClick={() => {
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({
                  behavior: "smooth",
                });
              }
            }}
            title="Scroll to latest"
          >
            <IoIosArrowDown size={20} />
          </div>
        )}
        <div
          ref={chatMessagesRef}
          className="flex-1  py-3 overflow-y-auto flex flex-col space-y-2"
        >
          {messages.map((_item, index) => {
            if (_item.role === "user") {
              return <HumanMessage key={index} message={_item.content} />;
            } else {
              return (
                <>
                  {index === messages.length - 1 && loading ? (
                    <>
                      <BotThinking key={index} text={streamingMsg} />
                    </>
                  ) : (
                    <>
                      <BotMessage
                        questionId={_item?.id && _item?.id}
                        key={index}
                        model={_item?.model ? _item?.model : null}
                        tokens={_item?.tokens ? _item?.tokens : null}
                        question={_item?.question ? _item?.question : ""}
                        trigger={_item?.intent ? _item?.intent : ""}
                        tagLoader={_item?.tagLoader ? _item?.tagLoader : null}
                        messageIndex={index}
                        lastMessageIndex={messages.length - 1}
                        message={_item.content}
                        feedback={_item.feedback}
                        refer={_item.refer}
                        isStreaming={isStreaming}
                      />
                      {trigger &&
                        !isStreaming &&
                        index === messages.length - 1 && (
                          <ChatOverlays intent={trigger} />
                        )}
                    </>
                  )}
                </>
              );
            }
          })}
          <div ref={messagesEndRef}></div>
        </div>
      </Box>
      <Box
        sx={{
          position: { xs: "fixed", lg: sidebarWeb ? "fixed" : "absolute" },
          bottom: 5,
          minHeight: "90px",
          width: "-webkit-fill-available",
          paddingRight: "40px",
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column-reverse",
        }}
      >
        <Box>
          {!isStreaming && messages?.length > 0 && (
            <>
              <MagicMycQResponse sendMessage={sendMessage} />
            </>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMaximizedChat ? `column` : "row",
              alignItems: "center",
              position: "relative",
            }}
          >
            {!isMaximizedChat && (
              <FilterSearch deleteAllMessages={deleteAllMessages} />
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
              className="w-full ms-2"
            >
              <ContentEditable
                className={`content-editable  !p-[12px_24px] !rounded-md ${
                  isStreaming && "cursor-wait"
                }  `}
                data-text="Send a message"
                innerRef={inputBoxRef}
                html={inputValue}
                onChange={handleChange}
              />
              <div className="absolute right-1">
                <FullscreenIcon
                  className="cursor-pointer"
                  color="primary"
                  onClick={() => {
                    setIsMaximizedChat(!isMaximizedChat);
                  }}
                />

                {streamingMsg ? (
                  <IconButton
                    className={"cursor-pointer"}
                    aria-label="Submit"
                    onClick={() => disconnectSocket()}
                  >
                    <StopCircleIcon
                      className={"cursor-pointer"}
                      color="primary"
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    aria-label="Submit"
                    disabled={inputValue.length > 0 ? false : true}
                    onClick={() => sendMessage(inputValue)}
                  >
                    <SendIcon className={"cursor-pointer"} color="primary" />
                  </IconButton>
                )}
              </div>
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog
        size="xl"
        open={isMaximizedChat}
        handler={() => {
          setIsMaximizedChat(!isMaximizedChat);
        }}
      >
        <DialogBody>
          <span className="flex justify-end items-center mb-2 cursor-pointer">
            <XMarkIcon
              className="w-5 h-5 "
              onClick={() => {
                setIsMaximizedChat(!isMaximizedChat);
              }}
            />
          </span>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMaximizedChat ? `column` : "row",
              alignItems: "center",
              position: "relative",
            }}
          >
            {!isMaximizedChat && <FilterSearch />}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
              className="w-full ms-2"
            >
              <ContentEditable
                className={`content-editable ${
                  isMaximizedChat && "!min-h-[48rem]"
                } !p-[12px_24px] !rounded-md ${isStreaming && "cursor-wait"} `}
                data-text="Send a message"
                innerRef={inputBoxRef}
                html={inputValue}
                onChange={handleChange}
              />
            </Box>
            {isMaximizedChat && (
              <div className="self-end mt-2">
                <Button
                  size="small"
                  aria-label="Submit"
                  variant="contained"
                  onClick={() => sendMessage(inputValue)}
                  endIcon={<SendIcon />}
                >
                  Send
                </Button>
              </div>
            )}
          </Box>
        </DialogBody>
      </Dialog>
    </>
  );
};

export default ChatUI;
